import { WORLD_WATCH_RPC, type WorldWatchInput } from "@cleo/contracts";
import { subscribe } from "@cleo/client-runtime/rpc";
import { runStreamInEnvironment } from "@cleo/client-runtime/state/runtime";
import * as Cause from "effect/Cause";
import * as Effect from "effect/Effect";
import * as Stream from "effect/Stream";
import { Atom } from "effect/unstable/reactivity";
import { connectionAtomRuntime } from "../connection/runtime";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { primaryEnvironmentIdAtom } from "../state/primaryEnvironment";

/** Keep persistent World updates off the browser's six HTTP/1 request slots. */
export async function watchPrimary(input: WorldWatchInput, signal: AbortSignal): Promise<Response> {
  signal.throwIfAborted();
  const environmentId = appAtomRegistry.get(primaryEnvironmentIdAtom);
  if (!environmentId) throw new Error("The Cleo connection is not ready.");
  let dispose = () => {};
  let closed = false;
  const body = new ReadableStream<Uint8Array>({
    start(controller) {
      const stop = () => {
        if (closed) return;
        closed = true;
        dispose();
        signal.removeEventListener("abort", stop);
        controller.close();
      };
      const work = runStreamInEnvironment(environmentId, subscribe(WORLD_WATCH_RPC, input)).pipe(
        Stream.runForEach((chunk) =>
          Effect.sync(() => {
            if (!closed) controller.enqueue(new TextEncoder().encode(chunk));
          }),
        ),
        Effect.andThen(Effect.sync(stop)),
        Effect.catchCause((cause) =>
          Effect.sync(() => {
            if (closed) return;
            closed = true;
            signal.removeEventListener("abort", stop);
            controller.error(Cause.squash(cause));
            dispose();
          }),
        ),
      );
      const atom = connectionAtomRuntime.atom(work).pipe(Atom.setIdleTTL(0));
      const unmount = appAtomRegistry.subscribe(atom, () => {}, { immediate: true });
      dispose = () => {
        unmount();
        signal.removeEventListener("abort", stop);
      };
      if (closed) {
        dispose();
        return;
      }
      signal.addEventListener("abort", stop, { once: true });
      if (signal.aborted) stop();
    },
    cancel() {
      closed = true;
      dispose();
    },
  });
  return new Response(body, { headers: { "content-type": "application/x-ndjson" } });
}
